import { api } from './api';
import { ResponseType } from '../types/shared.types';
import { GetFinancesOverviewParams } from '../types/finances.types';
import {
  CancelDebtRepaymentPlanRequest,
  CollectionLetterSummary,
  DebtRepaymentPlanRequest,
  DebtRepaymentSuggestion,
  DebtRepaymentSuggestionRequest,
  DebtRepaymentsPage,
  Receivables,
  ReceivablesFilter,
  ReceivablesOverview,
  ReceivablesLocationDetail,
  ResolveOutstandingItemRequest,
} from '../types/api/receivables.types';

/**
 * Admin receivables: what every location owes vGrubs.
 *
 * Reads are admin and support. Every write is admin only and carries
 * `adminPassword` - the receivables password, not the login one - and answers
 * with the location's whole detail, so the screen can take it as it is. A
 * wrong password is a 400 "Invalid admin password."
 *
 * See `types/api/receivables.types` for the shapes.
 */

export const getReceivables = (filter: ReceivablesFilter = 'all') =>
  api.get<ResponseType<Receivables>>('/v1/finances/receivables', {
    params: filter === 'all' ? undefined : { filter },
  });

/** The Finances tab's "Outstanding Balances" card. */
export const getReceivablesOverview = (params: GetFinancesOverviewParams) =>
  api.get<ResponseType<ReceivablesOverview>>(
    '/v1/finances/receivables/overview',
    { params }
  );

/** The current balance as the merchant sees it (plus old vDrive items), and every item. */
export const getReceivablesLocation = (locationId: number | string) =>
  api.get<ResponseType<ReceivablesLocationDetail>>(
    `/v1/finances/receivables/locations/${locationId}`
  );

/**
 * The collection letter for a deleted location that still owes: the letter
 * with the three ways to settle, then the statement of account. Base64 PDF
 * (no `data:` prefix), rendered on the spot and saved nowhere, so every call
 * is a new letter dated today. Admin only (support gets 403); 400 when the
 * location is not deleted or owes nothing.
 *
 * A big statement runs to ~60 pages and ~10 MB of base64, hence the timeout.
 */
export const getCollectionLetter = (locationId: number) =>
  api.get<ResponseType<string>>(
    `/v1/finances/receivables/locations/${locationId}/collection-letter`,
    { timeout: 120_000 }
  );

/**
 * The collection letter as a call script for billing: what the location owes
 * and why, what it already paid, the two options, what happens next and
 * answers to the usual objections. Same amounts as a letter generated today,
 * saved nowhere. Admin and support; the same 400s as the letter.
 *
 * No PDF to render, but the same queries (up to 5000 deliveries), hence the
 * timeout.
 */
export const getCollectionLetterSummary = (locationId: number) =>
  api.get<ResponseType<CollectionLetterSummary>>(
    `/v1/finances/receivables/locations/${locationId}/collection-letter/summary`,
    { timeout: 60_000 }
  );

/** Paid outside the system - a check, a wire, cash. The note says which. */
export const markOutstandingItemPaid = (
  itemId: number,
  data: ResolveOutstandingItemRequest
) =>
  api.post<ResponseType<ReceivablesLocationDetail>>(
    `/v1/finances/receivables/items/${itemId}/mark-paid`,
    data
  );

/** Will not be collected. */
export const writeOffOutstandingItem = (
  itemId: number,
  data: ResolveOutstandingItemRequest
) =>
  api.post<ResponseType<ReceivablesLocationDetail>>(
    `/v1/finances/receivables/items/${itemId}/write-off`,
    data
  );

/**
 * Debt repayment plans. Admin only, and unlike the item routes they take no
 * receivables password. Each answers with the location's whole detail.
 */
export const createDebtRepaymentPlan = (
  locationId: number,
  data: DebtRepaymentPlanRequest
) =>
  api.post<ResponseType<ReceivablesLocationDetail>>(
    `/v1/finances/receivables/locations/${locationId}/repayment-plan`,
    data
  );

/** The whole plan again, not just what changed. Only an active plan. */
export const updateDebtRepaymentPlan = (
  planId: number,
  data: DebtRepaymentPlanRequest
) =>
  api.put<ResponseType<ReceivablesLocationDetail>>(
    `/v1/finances/receivables/repayment-plans/${planId}`,
    data
  );

/** What was collected stays repaid. */
export const cancelDebtRepaymentPlan = (
  planId: number,
  data: CancelDebtRepaymentPlanRequest
) =>
  api.post<ResponseType<ReceivablesLocationDetail>>(
    `/v1/finances/receivables/repayment-plans/${planId}/cancel`,
    data
  );

/**
 * The AI's plan for a location: methods, amounts and how long repaying would
 * take, from the last 3 full months. Admin only, no receivables password,
 * nothing saved. Takes a few seconds. 400 when there is nothing a plan can
 * repay (or the body is out of range), 500 "AI generation failed."
 */
export const suggestDebtRepaymentPlan = (
  locationId: number,
  data: DebtRepaymentSuggestionRequest
) =>
  api.post<ResponseType<DebtRepaymentSuggestion>>(
    `/v1/finances/receivables/locations/${locationId}/repayment-plan/suggestion`,
    data
  );

/** Every collection of a location, any plan, newest first. Admin and support. */
export const getLocationRepayments = (
  locationId: number,
  params: { page: number; limit: number }
) =>
  api.get<ResponseType<DebtRepaymentsPage>>(
    `/v1/finances/receivables/locations/${locationId}/repayments`,
    { params }
  );
